import { Card, Muted, Stack, color, radius, space } from '@biopeak/ui';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase.ts';

/**
 * O que o PACIENTE disse sobre os próprios números.
 *
 * A OUTRA METADE DA CONTESTABILIDADE. O app do paciente já deixa ele explicar
 * um dia ("foi treino, não estresse"); sem esta tela a nota morria no banco, e
 * a feature seria meio caminho — alguém escreve e ninguém lê.
 *
 * POR QUE ISTO É DADO CLÍNICO, e não recado. O sensor registra que a
 * frequência cardíaca subiu; ele não sabe se foi corrida, febre, café ou o
 * relógio escorregando. Sem a nota, o médico só descobre perguntando na
 * consulta seguinte — meses depois, quando ninguém lembra. A nota foi escrita
 * no dia, por quem estava lá.
 *
 * O MÉDICO NÃO EDITA. A política de RLS só dá select a ele, e isso é
 * deliberado: a nota é a voz do paciente sobre o próprio dia. Um médico
 * corrigindo o relato transformaria a coisa em outra — se ele discorda,
 * o lugar é o prontuário e a conversa.
 */

const MOTIVO_LABEL: Record<string, string> = {
  exercicio: 'Foi exercício',
  doenca: 'Estava doente',
  medicacao: 'Mudança de remédio',
  estresse: 'Dia estressante',
  bebida_alcool: 'Bebeu álcool',
  viagem: 'Estava viajando',
  aparelho: 'Falha do aparelho',
  outro: 'Outro motivo',
};

interface NotaDoPaciente {
  id: string;
  metric_key: string | null;
  reference_date: string;
  motivo: string;
  observacao: string | null;
  metric_catalog: { display_name: string } | null;
}

export function PatientNotes({ patientId }: { patientId: string }) {
  const notas = useQuery({
    queryKey: ['patient-notes', patientId],
    queryFn: async (): Promise<NotaDoPaciente[]> => {
      const { data, error } = await supabase
        .from('patient_metric_notes')
        .select('id, metric_key, reference_date, motivo, observacao, metric_catalog(display_name)')
        .eq('patient_id', patientId)
        .order('reference_date', { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as unknown as NotaDoPaciente[];
    },
  });

  if (notas.isLoading) return <Muted>Carregando…</Muted>;

  // Sem nota não é falha nem pendência — a maioria dos dias não precisa de
  // explicação. Um card vazio aqui viraria ruído permanente na tela.
  if (!notas.data || notas.data.length === 0) return null;

  return (
    <Card>
      <Stack gap={space.md}>
        <Stack gap="2px">
          <span style={{ fontSize: '16px', fontWeight: 600 }}>
            O que o paciente explicou
          </span>
          <Muted style={{ fontSize: '13px' }}>
            Contexto que ele registrou sobre dias específicos. Não altera a
            medição — fica ao lado dela.
          </Muted>
        </Stack>

        <Stack gap={space.sm}>
          {notas.data.map((n) => (
            <div
              key={n.id}
              style={{
                display: 'flex',
                gap: space.md,
                paddingBottom: space.sm,
                borderBottom: `1px solid ${color.border}`,
              }}
            >
              <Stack gap="2px" style={{ minWidth: '92px', flexShrink: 0 }}>
                <span style={{ fontSize: '14px', fontWeight: 600 }}>
                  {formatarDia(n.reference_date)}
                </span>
                {n.metric_catalog?.display_name && (
                  <Muted style={{ fontSize: '12px' }}>{n.metric_catalog.display_name}</Muted>
                )}
              </Stack>

              <Stack gap="2px" style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    alignSelf: 'flex-start',
                    padding: `2px ${space.sm}`,
                    borderRadius: radius.pill,
                    background: `${color.accent}14`,
                    color: color.accent,
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {MOTIVO_LABEL[n.motivo] ?? n.motivo}
                </span>
                {n.observacao && (
                  <span style={{ fontSize: '14px', lineHeight: 1.45 }}>{n.observacao}</span>
                )}
              </Stack>
            </div>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
}

function formatarDia(iso: string): string {
  const [ano, mes, dia] = iso.split('-').map(Number);
  if (!ano || !mes || !dia) return iso;
  return new Date(ano, mes - 1, dia).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
}
